import { CommonModule } from '@angular/common';
import {
  Component,
  computed,
  OnInit,
  signal,
  ViewChild,
  ChangeDetectionStrategy
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import {
  AwBreadCrumbComponent,
  AwButtonDirective,
  AwDialogComponent,
  AwDividerComponent,
  AwSearchComponent,
  AwSpinnerComponent,
  AwTableComponent,
  AwToastComponent,
  BreadCrumb,
  DialogFormControls,
  DialogOptions,
  TableCellInput,
  TableCellTypes,
  TableRow,
  ToastVariants,
} from '@assetworks-llc/aw-component-lib';
import { CustomerService } from '../../../../../core/services/customer.service';
import {
  CreateCommunicationPropertiesRequest,
  Customer,
  UpdateCommunicationPropertiesRequest,
} from '../../../../../api/models';
import { OperationTypes } from '../../../../../shared/constants/operation-types.constants';
import {
  catchError,
  concatMap,
  finalize,
  from,
  map,
  of,
  switchMap,
  take,
  toArray,
} from 'rxjs';
import { MatIcon } from '@angular/material/icon';
import { FormsModule } from '@angular/forms';
import { ThemeService } from '../../../../../core/services/theme.service';
import { CommunicationPropertiesService } from '../../../../../core/services/communication-properties.service';
import { Organization, UserService } from '../../../../../core/services/user.service';
import {ClientSecretService} from '../../../../../core/services/client-secret.service';
import { ApiAccessSectionComponent } from '../../../../../shared/components/api-access-section/api-access-section.component';

@Component({
    selector: 'app-customer-details',
    imports: [
        CommonModule,
        AwBreadCrumbComponent,
        AwDividerComponent,
        AwSearchComponent,
        AwTableComponent,
        AwSpinnerComponent,
        AwDialogComponent,
        AwToastComponent,
        AwButtonDirective,
        MatIcon,
        FormsModule,
        ApiAccessSectionComponent,
    ],
    templateUrl: './customer-details.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrls: [
        './customer-details.component.scss',
        '../../../../../../styles/management.scss',
    ]
})
export class CustomerDetailsComponent implements OnInit {
  AuthTypeMap = {
    0: 'OAuth2',
    1: 'EntraID',
    2: 'OpenID Connect',
    3: 'N/A',
  } as const;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private customerService: CustomerService,
    private communicationPropService: CommunicationPropertiesService,
    private themeService: ThemeService,
    private userService: UserService,
    private clientSecretService: ClientSecretService
  ) {}

  @ViewChild(AwToastComponent) toastComponent!: AwToastComponent;
  @ViewChild(AwTableComponent, { static: true })
  awTableComponent!: AwTableComponent;

  isLoading: boolean = false;
  openDeleteOperationDialogSingle: boolean = false;
  openDeleteOperationDialogMultiple: boolean = false;

  container_theme = computed(() => this.themeService.themeSignal());

  userPermissions: Record<string, boolean> = {};
  organization: Organization | null = null;

  operationInfo: any[] = [];
  singleOperationToDelete: TableRow | null = null;
  multipleOperationsToDelete: TableRow[] | null = null;

  customerInfo!: Customer | undefined;
  customerInfoDividers = signal<any[]>([]);
  customerDateDividers = signal<any[]>([]);

  operationsTableData = signal<any[]>([]);
  operationsTableDataCopy: any[] = [];
  disableOperationsDelete: boolean = true;
  operationSearchValue!: string;
  checkedRows: TableRow[] = [];
  addOperationMode: 'custom' | 'api' | 'signalr' = 'custom';

  openAddInfoDialog: boolean = false;
  openEditInfoDialog: boolean = false;
  disableOperationsEdit: boolean = true;
  getFormFieldsData(): DialogFormControls {
    const controls: any[] = [
      {
        formControlName: 'operation_name',
        label: 'New Operation Name',
        value: '',
        type: 'text',
        controlRequired: true,
        validators: {
          required: {
            value: true,
            message: 'Please Enter a value',
          },
        },
      },
    ];

    if (this.addOperationMode !== 'signalr') {
      controls.push({
        formControlName: 'callback_endpoint',
        label: 'New Operation Callback HTTP Endpoint',
        value: '',
        type: 'text',
        controlRequired: true,
        validators: {
          required: {
            value: true,
            message: 'Please Enter a value',
          },
        },
      });
    }

    return { controls };
  }

  getDialogOptions(): DialogOptions {
    const title = this.addOperationMode === 'signalr'
      ? 'Add SignalR Operation'
      : 'Add Operation';
    const description = this.addOperationMode === 'signalr'
      ? 'Enter the name for the new SignalR operation'
      : 'Enter all nessecary information for your new operation here';
    return {
      variant: 'standard',
      title,
      description,
      secondaryButtonLabel: 'Cancel',
      primaryButtonLabel: 'Save',
    };
  }
  dialogOptionsDeleteSingle: DialogOptions = {
    variant: 'alert',
    title: `Delete Operation`,
    description:
      'By selecting Delete, you will permanently delete this information. Choose Cancel, to go back to the view.',
    secondaryButtonLabel: 'Cancel',
    primaryButtonLabel: 'Delete',
  };

  dialogOptionsDeleteMultiple: DialogOptions = {
    variant: 'alert',
    title: `Delete Operation(s)`,
    description:
      'By selecting Delete, you will permanently delete this information. Choose Cancel, to go back to the view.',
    secondaryButtonLabel: 'Cancel',
    primaryButtonLabel: 'Delete',
  };

  editFormFieldsData(): DialogFormControls {
    const rawName = this.checkedRows.length === 1 ? this.checkedRows[0]['endpoint_name'] : '';
    const displayName = rawName.startsWith('API-') ? rawName.slice(4) : rawName;
    const isSignalR = this.checkedRows.length === 1
      && this.checkedRows[0]['endpoint_type'] === OperationTypes.SIGNALR;

    const controls: any[] = [
      {
        formControlName: 'operation_name',
        label: 'Operation Name',
        value: displayName,
        type: 'text',
        controlRequired: true,
        validators: {
          required: {
            value: true,
            message: 'Please Enter a value',
          },
        },
      },
    ];

    if (!isSignalR) {
      controls.push({
        formControlName: 'callback_endpoint',
        label: 'Callback HTTP Endpoint',
        value: this.checkedRows.length === 1 ? this.checkedRows[0]['endpoint_callback'] : '',
        type: 'text',
        controlRequired: true,
        validators: {
          required: {
            value: true,
            message: 'Please Enter a value',
          },
        },
      });
    }

    return { controls };
  }

  editDialogOptions(): DialogOptions {
    return {
      variant: 'standard',
      title: 'Edit Operation',
      description: 'Update the operation information below',
      secondaryButtonLabel: 'Cancel',
      primaryButtonLabel: 'Save',
    };
  }

  headerButtonActions = signal<any[]>([
    {
      iconButtonCallback: {
        icon: 'edit',
        label: 'Edit customer',
        action: () => {
          this.router.navigate(['edit'], { relativeTo: this.route });
        },
      },
    },
  ]);

  operationsColumnsDefinition: TableCellInput[] = [
    {
      type: TableCellTypes.Title,
      key: 'endpoint_name',
      label: 'Endpoint Name',
      isExpanded: false,
      sort: true,
      align: 'left',
    },
    {
      type: TableCellTypes.Title,
      key: 'endpoint_type',
      label: 'Type',
      isExpanded: false,
      sort: true,
      align: 'left',
    },
    {
      type: TableCellTypes.Title,
      key: 'endpoint_id',
      label: 'Endpoint ID',
      isExpanded: false,
      sort: true,
      align: 'left',
    },
    {
      type: TableCellTypes.Title,
      key: 'endpoint_callback',
      label: 'Callback HTTP Endpoint',
      isExpanded: false,
      sort: true,
      align: 'left',
    },
  ];

  breadcrumbs = signal<BreadCrumb[]>([
    {
      label: 'Customer Management',
      route: '/customer-management/overview',
      newTab: false,
    },
  ]);

  buildAllData(): void {
    this.isLoading = true;
    const userPermissions = this.userService.getUserPermissions();

    this.route.paramMap
      .pipe(
        map((params) => {
          this.breadcrumbs.set([
            ...this.breadcrumbs(),
            {
              label: 'Customer Details',
              route: `/customer-management/customer-details/${params.get(
                'customerId'
              )!}`,
              newTab: false,
            },
          ]);
          return params.get('customerId')!;
        }),
        take(1),
        switchMap((customerId) =>
          this.customerService
            .getCustomerById({ organizationId: customerId })
            .pipe(
              catchError((error) => {
                this.buildToastMessage(
                  'Failed to retrieve customer info',
                  'error'
                );
                return of(null);
              })
            )
        ),
        switchMap((customerInfo) => {
          if (customerInfo) {
            this.customerInfo = customerInfo;

            return this.communicationPropService
              .getCommunicationPropertiesById({
                organizationId: customerInfo.organizationId || '',
              })
              .pipe(
                catchError((error) => {
                  return of(null);
                })
              );
          }
          return of(null);
        }),
        switchMap((communicationProps) => {
          if (this.customerInfo?.organizationId && userPermissions['isAdministrator']) {
            return this.clientSecretService
              .getClientSecrets({
                organizationId: this.customerInfo.organizationId
              })
              .pipe(
                map((secrets) => ({ communicationProps, secrets })),
                catchError(() => of({ communicationProps, secrets: undefined }))
              );
          }
          return of({ communicationProps, secrets: undefined });
        }),
        finalize(() => {
          this.isLoading = false;
        })
      )
      .subscribe(({ communicationProps, secrets }) => {
        let operationData: any[] = [];
        if (communicationProps) {
          operationData =
            communicationProps.operations?.map((operation) => ({
              endpoint_name: operation.name ?? 'Not Found',
              endpoint_type: operation.operationType === OperationTypes.SIGNALR ? OperationTypes.SIGNALR : OperationTypes.HTTP,
              endpoint_id: operation.id ?? 'Not Found',
              endpoint_callback: operation.callbackHttpEndpoint ?? 'Not Found',
            })) ?? [];
        }

        this.operationsTableData.set(operationData);
        this.operationsTableDataCopy = operationData;

        const infoDividers = [
          {
            title: 'Customer Name',
            info: this.customerInfo?.organizationName,
          },
          {
            title: 'Customer ID',
            info: this.customerInfo?.organizationId,
          },
          {
            title: 'Customer Status',
            info: this.customerInfo?.enabled ? 'Active' : 'Disabled',
          },
        ];

        if (this.organization === 'assetworks-employee' && this.userPermissions['isAdministrator']) {
          infoDividers.push({
            title: 'Client Secret Expiration Date',
            info: secrets && Object.keys(secrets).length > 0
              ? (() => {
                  const dates = Object.values(secrets).map(date => new Date(date)).filter(date => !isNaN(date.getTime()));
                  return dates.length > 0
                    ? new Date(Math.max(...dates.map(date => date.getTime()))).toLocaleDateString()
                    : 'No valid expiration dates';
                })()
              : 'No client secret expiration dates found',
          });
        }

        this.customerInfoDividers.set(infoDividers);

        this.customerDateDividers.set([
          {
            title: 'Created Date',
            info: this.customerInfo?.createdOn
              ? new Date(this.customerInfo?.createdOn).toLocaleDateString()
              : 'N/A',
          },
          {
            title: 'Last Modified Date',
            info: this.customerInfo?.modifiedOn
              ? new Date(this.customerInfo?.modifiedOn).toLocaleDateString()
              : 'N/A',
          },
        ]);
      });
  }

  ngOnInit(): void {
    this.organization = this.userService.getOrganization();
    this.userPermissions = this.userService.getUserPermissions();

    if (this.userPermissions['canDelete'] || this.userPermissions['canEdit']) {
      this.operationsColumnsDefinition.unshift({
        type: TableCellTypes.Checkbox,
        key: 'checkbox',
        isExpanded: false,
        align: 'center',
      });
    }

    if (this.userPermissions['canDelete']) {
      this.operationsColumnsDefinition.push({
        type: TableCellTypes.IconButton,
        key: 'random',
        label: 'Remove',
        buttonType: 'primary',
        isExpanded: false,
        icon: 'delete',
        action: (data: any) => {
          this.openDeleteOperationDialogSingle = true;
          this.singleOperationToDelete = data;
        },
      });
    }

    this.buildAllData();
  }

  checkBoxActionHandler(checkedRows: { rows: TableRow[]; key: string }) {
    this.checkedRows = checkedRows.rows;
    this.disableOperationsEdit = this.checkedRows.length !== 1;
    if (this.checkedRows.length > 0) {
      this.disableOperationsDelete = false;
      this.multipleOperationsToDelete = this.checkedRows;
    } else {
      this.disableOperationsDelete = true;
    }
  }
  onSearchTable(searchInput: string) {
    this.awTableComponent.applyFilter(searchInput);
  }
  dialogFormCloseHandler(key: string) {
    if (key === 'add') {
      this.openAddInfoDialog = false;
      this.addOperationMode = 'custom';
    } else if (key === 'edit') {
      this.openEditInfoDialog = false;
    } else if (key === 'delete-single') {
      this.openDeleteOperationDialogSingle = false;
    } else if (key === 'delete-multiple') {
      this.openDeleteOperationDialogMultiple = false;
    }
  }

  buildToastMessage(message: string, variant: ToastVariants): void {
    this.toastComponent.showToast({
      variant: variant,
      title: message,
      description: '',
      removeIcon: true,
      removeX: false,
      alwaysShow: false,
      toastPositionTopLeft: false,
      multiToasts: false,
    });
  }

  addOperationFormSubmit(formValues: any) {
    this.isLoading = true;
    if (formValues.valid) {
      const formData: any = formValues.formData;
      const operationName = this.addOperationMode === 'api'
        ? `API-${formData.operation_name}`
        : formData.operation_name;
      const isSignalR = this.addOperationMode === 'signalr';
      const callbackEndpoint = isSignalR ? undefined : formData.callback_endpoint;
      const operationType = isSignalR ? OperationTypes.SIGNALR : OperationTypes.HTTP;

      const newCommunicationProp: CreateCommunicationPropertiesRequest = {
        operations: [
          { callbackHttpEndpoint: callbackEndpoint, name: operationName, operationType },
        ],
      };

      this.communicationPropService
        .createCommunicationProperties({
          organizationId: this.customerInfo?.organizationId || '',
          body: newCommunicationProp,
        })
        .pipe(
          catchError((error) => {
            this.buildToastMessage('Failed to add operation', 'error');
            return of(null);
          }),
          finalize(() => {
            this.isLoading = false;
          })
        )
        .subscribe((props) => {
          if (!props) {
            this.buildToastMessage('Failed to add operation', 'error');
            return;
          }

          const newOperation = props.operations?.[props.operations.length - 1];
          const newOperationId = newOperation?.id ?? '';

          this.openAddInfoDialog = false;
          this.addOperationMode = 'custom';
          this.operationsTableData.set([
            ...this.operationsTableData(),
            {
              endpoint_name: operationName,
              endpoint_type: operationType,
              endpoint_id: newOperationId,
              endpoint_callback: callbackEndpoint ?? '',
            },
          ]);
          this.operationsTableDataCopy = [...this.operationsTableData()];
          this.buildToastMessage('Successfully added operation', 'success');
        });
    } else {
      this.buildToastMessage('Fill in all required fields', 'error');
      this.isLoading = false;
    }
  }

  editOperationFormSubmit(formValues: any): void {
    this.isLoading = true;
    if (formValues.valid) {
      const formData: any = formValues.formData;
      const isApiOperation = this.checkedRows[0]['endpoint_name']?.startsWith('API-');
      const operationName = isApiOperation
        ? `API-${formData.operation_name}`
        : formData.operation_name;
      const callbackEndpoint = formData.callback_endpoint;
      const operationId = this.checkedRows[0]['endpoint_id'];

      const updateRequest: UpdateCommunicationPropertiesRequest = {
        operation: { name: operationName, callbackHttpEndpoint: callbackEndpoint },
      };

      this.communicationPropService
        .updateCommunicationPropertiesOperation({
          organizationId: this.customerInfo?.organizationId || '',
          operationId: operationId,
          body: updateRequest,
        })
        .pipe(
          finalize(() => {
            this.isLoading = false;
          })
        )
        .subscribe((success) => {
          if (success) {
            this.openEditInfoDialog = false;
            const updated = this.operationsTableDataCopy.map((row) =>
              row['endpoint_id'] === operationId
                ? { ...row, endpoint_name: operationName, endpoint_callback: callbackEndpoint }
                : row
            );
            this.operationsTableData.set(updated);
            this.operationsTableDataCopy = [...updated];
            this.buildToastMessage('Successfully updated operation', 'success');
          } else {
            this.buildToastMessage('Failed to update operation', 'error');
          }
        });
    } else {
      this.buildToastMessage('Fill in all required fields', 'error');
      this.isLoading = false;
    }
  }

  deleteOperationFormSubmit(): void {
    if (this.singleOperationToDelete === null) {
      this.buildToastMessage('No operation selected for deletion', 'error');
      return;
    }
    const operationId: string = this.singleOperationToDelete['endpoint_id'];

    this.isLoading = true;
    this.communicationPropService
      .deleteCommunicationPropertiesOperation({
        organizationId: this.customerInfo?.organizationId || '',
        operationId: operationId,
      })
      .pipe(
        switchMap((operationDeleted) => {
          if (!operationDeleted) {
            this.buildToastMessage('Failed to delete operation', 'error');
            return of({
              commPropsDeleteAttempted: false,
              commPropsDeleted: false,
            });
          }

          this.buildToastMessage('Successfully deleted operation', 'success');
          const filtered = this.operationsTableDataCopy.filter(
            (op) => op['endpoint_id'] !== operationId
          );
          this.operationsTableData.set(filtered);
          this.operationsTableDataCopy = [...filtered];

          if (filtered.length === 0) {
            return this.communicationPropService
              .deleteCommunicationProperties({
                organizationId: this.customerInfo?.organizationId || '',
              })
              .pipe(
                map((deletedAll) => ({
                  commPropsDeleteAttempted: true,
                  commPropsDeleted: deletedAll,
                }))
              );
          }

          return of({
            commPropsDeleteAttempted: false,
            commPropsDeleted: false,
          });
        }),

        finalize(() => {
          this.isLoading = false;
          this.openDeleteOperationDialogSingle = false;
        })
      )
      .subscribe(({ commPropsDeleteAttempted, commPropsDeleted }) => {
        if (commPropsDeleteAttempted && commPropsDeleted) {
          this.buildToastMessage(
            'Successfully deleted communication properties',
            'success'
          );
        }
      });
  }

  deleteSelectedOperations(): void {
    if (!this.checkedRows.length) {
      this.buildToastMessage('No rows selected', 'error');
      return;
    }

    const operationsToDelete: any[] = this.checkedRows.map(
      (row) => row['endpoint_id']
    );
    const customerId: string = this.customerInfo?.organizationId || '';

    this.isLoading = true;
    from(operationsToDelete)
      .pipe(
        concatMap((operationId) =>
          this.communicationPropService
            .deleteCommunicationPropertiesOperation({
              organizationId: customerId,
              operationId: operationId,
            })
            .pipe(
              map(() => ({ operationId, deleted: true })),
              catchError(() => of({ operationId, deleted: false }))
            )
        ),
        toArray(),
        finalize(() => {
          this.isLoading = false;
          this.openDeleteOperationDialogMultiple = false;
        })
      )
      .subscribe((results) => {
        const succeeded = results
          .filter((r) => r.deleted)
          .map((r) => r.operationId);
        const filtered = this.operationsTableDataCopy.filter(
          (row) => !succeeded.includes(row['endpoint_id'])
        );
        this.operationsTableData.set(filtered);
        this.operationsTableDataCopy = [...filtered];

        if (filtered.length === 0) {
          this.communicationPropService
            .deleteCommunicationProperties({
              organizationId: this.customerInfo?.organizationId || '',
            })
            .subscribe((propsDeleted) => {
              this.buildToastMessage(
                propsDeleted
                  ? 'Successfully deleted communication properties'
                  : 'Failed to delete communication properties',
                propsDeleted ? 'success' : 'error'
              );
            });
        } else {
          const total = operationsToDelete.length;
          const num = succeeded.length;
          this.buildToastMessage(
            num === total
              ? 'Successfully deleted all selected operations'
              : `Deleted ${num} of ${total} operations`,
            num === total ? 'success' : 'alert'
          );
        }
      });
  }
}
